import { MessageFeedbackType } from "@/constants/chat.constant";
import { formatDate } from "@/utils/date.utils";
import clsx from "clsx";
import { ThumbsDown, ThumbsUpIcon } from "lucide-react";

/**
 * Props for the FeedbackCard component
 */
interface FeedbackCardProps {
  /** Message data with feedback information */
  message: any;
  /** Whether card is selected. Defaults to false. */
  selected?: boolean;
}

// Date formatting constant
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  day: "2-digit",
  month: "short",
  year: "numeric",
};

/**
 * FeedbackCard component displays a message feedback card with thumbs up/down
 * icons, timestamp, and message preview. Shows selected state styling when selected.
 * Used in feedback manager sidebar.
 * 
 * @component
 * @param {FeedbackCardProps} props - Component props
 * @param {any} props.message - Message data with feedback
 * @param {boolean} [props.selected=false] - Whether card is selected
 * @returns {JSX.Element} Feedback card with icons, timestamp, and preview
 * 
 * @example
 * ```tsx
 * <FeedbackCard
 *   message={message}
 *   selected={isSelected}
 * />
 * ```
 */
export default function FeedbackCard({
  message,
  selected = false,
}: FeedbackCardProps) {
  const formatDateString = (utcString: string) => {
    if (!utcString) return "—";
    return formatDate(utcString)
  };

  return (
    <div
      className={clsx(
        "flex items-start gap-2 border border-card-border rounded-2xl p-3 w-full transition-all overflow-hidden",
        selected
          ? "bg-card-background_selected"
          : "bg-card-background hover:bg-gray-50"
      )}
    >
      {/* Feedback Icons */}
      <div className="flex gap-2 px-2 py-1 flex-shrink-0">
        <ThumbsUpIcon
          size={16}
          className={clsx(
            "text-slate-500",
            message.feedback?.feedback_type === MessageFeedbackType.POSITIVE
              ? "fill-primary-500"
              : ""
          )}
        />
        <ThumbsDown
          size={16}
          className={clsx(
            "text-slate-500",
            message.feedback?.feedback_type === MessageFeedbackType.NEGATIVE
              ? "fill-primary-500"
              : ""
          )}
        />
      </div>

      {/* Content */}
      <div className="flex flex-col w-full min-w-0 justify-between gap-y-1">
        {/* (Session + Prompt) */}
        <div className="flex flex-row items-center gap-2 flex-[1_1_auto] min-w-0 overflow-hidden">
          <span className="font-medium text-sm uppercase text-primary-500 truncate min-w-0">
            {message.session_id}
          </span>
          <span className="text-slate-600"> | </span>
          <span className="text-base font-semibold text-slate-800 truncate flex-1 min-w-0">
            {message.prompt}
          </span>
        </div>

        {/* (Date + Status) */}
        <div className="flex flex-row items-center gap-2 flex-shrink-0 min-w-fit">
          <span className="text-slate-600 md:text-sm text-xs whitespace-nowrap">
            {formatDateString(message.created_at)}
          </span>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium border border-warning-text text-warning-text bg-warning-background capitalize whitespace-nowrap">
            {message.review_status?.toLowerCase()} Review
          </span>
        </div>
      </div>
    </div>
  );
}
